<script lang="ts">
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import LayoutStore from "@shared/svelte/Stores/LayoutStore";
  import {
    DistributorEntity,
    OrderEntity,
    ManualOrderActionEntity,
  } from "@shared/services/event_tracking/entities";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";
  import CurrentRetailerStore from "@retailer/svelte/stores/CurrentRetailerStore";
  import CurrentUserStore from "@shared/svelte/Stores/CurrentUserStore";

  import {
    copyOrderToClipboard,
    downloadPdf,
    buildMailtoLink,
    formatSalesRepEmails,
  } from "./OrderHelpers";
  import { createEventDispatcher } from "svelte";
  import { cloneDeep } from "lodash";

  import type { SharedOrder } from "@shared/types/Order";
  import type { RetailerType } from "@shared/types/Retailer";

  export let retailer: RetailerType;
  export let order: SharedOrder;

  const dispatch = createEventDispatcher();
  const { trackClick } = EventTracking;

  const markOrderAsSent = (_e: Event) => {
    const updatedOrder = cloneDeep(order);
    updatedOrder.manual_order_action_required = false;

    dispatch("orderUpdated", { order: updatedOrder });
    trackClick(null, [
      new OrderEntity({ id: order.id }),
      new DistributorEntity({ id: order.distributor.id }),
      new ManualOrderActionEntity({ action_type: "clicks_mark_as_sent", action_result: false }),
    ]);
  };

  const salesRepAddresses = formatSalesRepEmails(order);

  $: showStreamlinedAOOActionRequired =
    stagedRolloutCheck("disco__aoo_streamlined_flow") &&
    $CurrentRetailerStore?.aoo_streamlined_flow;
</script>

<style>
  .pointer:hover {
    cursor: pointer;
  }

  .button-margin {
    margin-right: 0 !important;
  }
</style>

<div class="fizz-surface-red fizz-text-medium" data-testid="action-required-banner">
    {#if showStreamlinedAOOActionRequired}
      <div class="fizz-container fizz-inset-square-16 fizz-flex-row fizz-align-content-center">
        <p class="flex gap-8 items-center flex-wrap">
          <strong class="flex items-center gap-8">
            <Icon type="Error" extraClassNames="fizz-icon-16 fizz-inline-4" height="16" width="16" />
            Action Required:
          </strong>
          Check your email ({$CurrentUserStore.email})
          for the next steps to send this order.
          Use this button to mark this order as sent for your records.
          <button
            class="fizz-button fizz-button-small fizz-button-icon button-margin"
            on:click={markOrderAsSent}
          >
            <span>Mark Order as Sent</span>
          </button>
        </p>
      </div>
    {:else}
      <div class="fizz-container fizz-inset-square-16 fizz-flex-col fizz-align-content-center">
        <p class="flex flex-wrap gap-4 fizz-stack-8">
          <strong class="flex inline-flex items-center gap-8">
            <Icon type="Error" extraClassNames="fizz-icon-16 fizz-inline-4" height="16" width="16" />
            Action Required:
          </strong>
          Orders to this distributor <strong>must be sent</strong> through your email. Use the
          buttons below to send the order and
          <a href={"#"} on:click|preventDefault={markOrderAsSent} class="pointer fizz-text-strong">
            mark this order as sent
          </a>
          for your records.
        </p>
        <div class="fizz-flex-row fizz-flex-wrap" style="--gap: 8px">
          {#if $LayoutStore.isDesktop}
            <button
              class="fizz-button fizz-button-small fizz-button-icon button-margin"
              on:click={() => downloadPdf(order)}
              on:click={() =>
                trackClick(null, [
                  new OrderEntity({ id: order.id }),
                  new DistributorEntity({ id: order.distributor.id }),
                  new ManualOrderActionEntity({ action_type: "clicks_download_pdf" }),
                ])}
            >
              <Icon type="Save" />
              <span>Download PDF</span>
            </button>
          {/if}

          <button
            class="fizz-button fizz-button-small fizz-button-icon button-margin"
            on:click={() => copyOrderToClipboard(order)}
            on:click={() =>
              trackClick(null, [
                new OrderEntity({ id: order.id }),
                new DistributorEntity({ id: order.distributor.id }),
                new ManualOrderActionEntity({ action_type: "clicks_copy_order" }),
              ])}
          >
            <Icon type="Copy" />
            <span>Copy order</span>
          </button>

          {#if salesRepAddresses.length}
            <a
              href={buildMailtoLink(order, retailer, salesRepAddresses)}
              on:click={() =>
                trackClick(null, [
                  new OrderEntity({ id: order.id }),
                  new DistributorEntity({ id: order.distributor.id }),
                  new ManualOrderActionEntity({ action_type: "clicks_email_rep" }),
                ])}
              class="fizz-button fizz-button-small fizz-button-icon button-margin"
            >
              <Icon type="MessageArrow" />
              <span>Send as email</span>
            </a>
          {/if}
        </div>
      </div>
    {/if}
</div>
